/**
 * Preview vs. render check: the server renders the frame at the playhead (headless Chromium, the
 * same engine) and the browser renders it at the same size; the pixels are compared.
 */

import { Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../api";
import type { Preview } from "./preview";
import { flush } from "./save";
import { useEditor } from "./store";

interface Result {
  t: number;
  differing: number;
  total: number;
  maxDiff: number;
}

export function CompareDialog({ preview }: { preview: Preview }) {
  const project = useEditor((s) => s.project)!;
  const [t] = useState(() => useEditor.getState().time);
  const [state, setState] = useState<"working" | "done" | "error">("working");
  const [msg, setMsg] = useState("Rendering on the server…");
  const [res, setRes] = useState<Result | null>(null);
  const local = useRef<HTMLCanvasElement>(null);
  const server = useRef<HTMLCanvasElement>(null);
  const diff = useRef<HTMLCanvasElement>(null);
  const close = () => useEditor.getState().set({ panel: null });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        await flush();
        let job = await api.still(project.id, t, "full");
        while (live && ["queued", "running"].includes(job.status)) {
          await new Promise((r) => setTimeout(r, 700));
          job = await api.job(job.id);
        }
        if (!live) return;
        if (job.status !== "done" || !job.output?.file) throw new Error(job.message || "render failed");
        setMsg("Comparing…");
        const img = new Image();
        img.src = fileUrl(project.id, job.output.file);
        await img.decode();
        const W = img.naturalWidth;
        const H = img.naturalHeight;
        const sc = server.current!;
        sc.width = W;
        sc.height = H;
        const sctx = sc.getContext("2d", { willReadFrequently: true })!;
        sctx.drawImage(img, 0, 0);
        const a = sctx.getImageData(0, 0, W, H);
        const b = preview.renderOffscreen(t, W, H);
        const lc = local.current!;
        lc.width = W;
        lc.height = H;
        lc.getContext("2d")!.putImageData(b, 0, 0);
        const dc = diff.current!;
        dc.width = W;
        dc.height = H;
        const dctx = dc.getContext("2d")!;
        const d = dctx.createImageData(W, H);
        let differing = 0;
        let maxDiff = 0;
        for (let i = 0; i < a.data.length; i += 4) {
          const m = Math.max(Math.abs(a.data[i] - b.data[i]), Math.abs(a.data[i + 1] - b.data[i + 1]), Math.abs(a.data[i + 2] - b.data[i + 2]));
          if (m > 0) differing++;
          if (m > maxDiff) maxDiff = m;
          const g = 255 - Math.round(((a.data[i] + a.data[i + 1] + a.data[i + 2]) / 3) * 0.15);
          d.data[i] = m ? 255 : g;
          d.data[i + 1] = m ? 0 : g;
          d.data[i + 2] = m ? 0 : g;
          d.data[i + 3] = 255;
        }
        dctx.putImageData(d, 0, 0);
        setRes({ t, differing, total: W * H, maxDiff });
        setState("done");
      } catch (e) {
        if (live) {
          setMsg((e as Error).message);
          setState("error");
        }
      }
    })();
    return () => {
      live = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal compare" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Preview vs. render at {t.toFixed(2)} s</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        {state !== "done" && <p className={state === "error" ? "bad" : "muted"}>{state === "working" && <Loader2 size={14} className="spin" />} {msg}</p>}
        {res && (
          <p>
            {res.differing === 0 ? (
              <strong className="good">Identical: all {res.total.toLocaleString()} pixels match.</strong>
            ) : (
              <>
                <strong className={res.differing / res.total < 0.001 ? "warn" : "bad"}>
                  {res.differing.toLocaleString()} of {res.total.toLocaleString()} pixels differ ({((100 * res.differing) / res.total).toFixed(3)}%), by up to {res.maxDiff}/255.
                </strong>{" "}
                <span className="muted small">Small differences are anti-aliasing in non-Chromium browsers; timing and placement are still the same.</span>
              </>
            )}
          </p>
        )}
        <div className="compare-grid" style={{ display: state === "done" ? undefined : "none" }}>
          <figure><canvas ref={local} /><figcaption>This browser</figcaption></figure>
          <figure><canvas ref={server} /><figcaption>Server render</figcaption></figure>
          <figure><canvas ref={diff} /><figcaption>Differences (red)</figcaption></figure>
        </div>
      </div>
    </div>
  );
}
